<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { deliveryLabels, errorText } from './api.js';

  export let messageId;
  export let api;
  const dispatch = createEventDispatcher();
  const labels = {
    confirm_accepted: 'Patvirtinti priėmimą pagal tiekėjo žurnalą',
    cancel: 'Sustabdyti tolesnius bandymus',
    resend_unknown: 'Sukurti naują siuntimą — galima kopija',
    retry_blocked: 'Leisti pakartoti po patikros',
    replace_blocked: 'Peržiūrėti naują žinutę eilės pabaigoje',
  };
  let snapshot = null, loading = true, saving = false, action = '', reason = '', providerId = '';
  let duplicateRisk = false, replacementConfirmed = false, error = '', success = '', alive = false, intent = null;
  $: ready = !loading && !saving && snapshot?.actions.includes(action) && !!reason.trim()
    && (action !== 'confirm_accepted' || !!providerId.trim()) && (action !== 'resend_unknown' || duplicateRisk)
    && (action !== 'replace_blocked' || (replacementConfirmed && !!snapshot.replacement_preview));

  async function load() {
    loading = true;
    try {
      const result = await api(`messages/${messageId}/recovery/`);
      if (!alive) return;
      snapshot = result;
      if (!result.actions.includes(action)) action = '';
    } catch (problem) { report(problem); }
    finally { if (alive) loading = false; }
  }

  function report(problem) {
    if (!alive || problem.name === 'AbortError') return;
    error = errorText(problem);
    if ([401, 403].includes(problem.status)) dispatch('denied', problem);
  }

  async function submit() {
    if (!ready) return;
    saving = true; error = ''; success = '';
    const body = { action, reason: reason.trim(), expected_version: snapshot.version,
      provider_id: action === 'confirm_accepted' ? providerId.trim() : '',
      confirm_duplicate_risk: action === 'resend_unknown' && duplicateRisk,
      ...(action === 'replace_blocked' ? { confirm_replacement: replacementConfirmed,
        expected_conversation_revision: snapshot.conversation_revision } : {}) };
    const fingerprint = JSON.stringify(body);
    if (intent?.fingerprint !== fingerprint) intent = { fingerprint, key: crypto.randomUUID() };
    try {
      await api(`messages/${messageId}/recovery/`, { method: 'POST', body: { ...body, idempotency_key: intent.key } });
      if (!alive) return;
      success = 'Sprendimas užregistruotas.';
      duplicateRisk = false;
      replacementConfirmed = false;
      dispatch('changed');
      await load();
    } catch (problem) {
      report(problem);
      if (alive && problem.code === 'recovery_conflict') {
        duplicateRisk = false;
        replacementConfirmed = false;
        action = '';
        await load();
      }
    } finally { if (alive) saving = false; }
  }

  onMount(() => { alive = true; void load(); return () => { alive = false; }; });
</script>

<section class="recovery" aria-label="Siuntimo patikra">
  <header><h3>Siuntimo patikra</h3><button type="button" on:click={() => dispatch('close')}>Uždaryti patikrą</button></header>
  <p>Sprendimas bus įrašytas į veiksmų žurnalą. Sustabdymas negali atšaukti jau perduotos žinutės.</p>
  {#if error}<p class="error" role="alert">{error}</p>{/if}
  {#if success}<p role="status">{success}</p>{/if}
  {#if loading}<p role="status">Tikrinama siuntimo būsena…</p>
  {:else if snapshot}
    <p>Būsena: {deliveryLabels[snapshot.state] || snapshot.state} · Bandymų: {snapshot.attempts}</p>
    {#if snapshot.ordering_reason}<p>{errorText({ code: snapshot.ordering_reason })}</p>{/if}
    {#if snapshot.actions.length}
      <form on:submit|preventDefault={submit}>
        <label>Sprendimas<select bind:value={action} disabled={saving} on:change={() => { duplicateRisk = false; replacementConfirmed = false; success = ''; }}>
          <option value="">Pasirinkite sprendimą</option>
          {#each snapshot.actions as item}<option value={item}>{labels[item] || item}</option>{/each}
        </select></label>
        <label>Patikros pagrindas<textarea bind:value={reason} maxlength="500" rows="2" disabled={saving} placeholder="Trumpas pagrindas arba tiekėjo patikros nuoroda; be klientų duomenų"></textarea></label>
        {#if action === 'confirm_accepted'}<label>Tiekėjo žinutės ID<input bind:value={providerId} maxlength="255" disabled={saving} /></label>{/if}
        {#if action === 'resend_unknown'}
          <label class="risk"><input type="checkbox" bind:checked={duplicateRisk} disabled={saving} />Suprantu, kad klientas gali gauti žinutę du kartus</label>
        {/if}
        {#if action === 'replace_blocked'}
          <section aria-label="Pakaitinės žinutės peržiūra"><p class="preview">{snapshot.replacement_preview || 'Turinys nepasiekiamas; šio siuntimo pakartoti negalima.'}</p></section>
          <label class="risk"><input type="checkbox" bind:checked={replacementConfirmed} disabled={saving} />Patikrinau turinį ir noriu naujos žinutės eilės pabaigoje</label>
        {/if}
        <button class="confirm" type="submit" disabled={!ready}>{saving ? 'Registruojama…' : 'Patvirtinti sprendimą'}</button>
      </form>
    {/if}
  {/if}
</section>

<style>
  .recovery{padding:16px 24px;border-bottom:1px solid #ead4ae;background:#fff8e9;max-height:45vh;overflow:auto;font-size:12px}
  header{display:flex;align-items:center;justify-content:space-between;gap:10px}h3{margin:0}p{line-height:1.5;margin:10px 0}form{display:flex;flex-direction:column;gap:10px}
  label{display:flex;flex-direction:column;gap:5px;font-weight:600}input,textarea,select,button{font:inherit;border:1px solid #bfb7a8;border-radius:6px;padding:8px;background:#fff;color:#25231f;box-sizing:border-box}textarea,select{width:100%}textarea{resize:vertical}
  button{cursor:pointer}button:disabled{opacity:.5;cursor:not-allowed}.confirm{align-self:flex-start;background:#ba4510;border-color:#ba4510;color:#fff}.risk{flex-direction:row;align-items:center}.risk input{width:18px;height:18px}.error{color:#922c19}
  .preview{white-space:pre-wrap;overflow-wrap:anywhere;background:#fff;padding:10px;border:1px solid #ead4ae}
  :focus-visible{outline:2px solid #d95513;outline-offset:2px}@media(max-width:760px){.recovery{padding:12px}input,textarea,select{font-size:16px}}
</style>
